import { useCallback, useMemo, useState } from 'react'
import { NetworkStatus } from '@apollo/client'

import { useDebounce } from 'hooks'
import { useDashboardsQueries } from 'hooks-queries/dashboards'
import { formatJobForFolderList } from 'utils/jobUtils'

import { GET_JOBS_WITH_FOLDERS_LOG, JOBS_PAGE_SIZE, SEARCH_DEBOUNCE_DELAY_MS } from '../const'

export function useJobsWithFolders({ workspaceId }) {
  const [searchTerm, setSearchTerm] = useState('')
  const [visibleCount, setVisibleCount] = useState(JOBS_PAGE_SIZE)
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')

  const { debouncedAction: debouncedSetSearchTerm } = useDebounce((newTerm: string) => {
    setDebouncedSearchTerm(newTerm)
    setVisibleCount(JOBS_PAGE_SIZE)
  }, SEARCH_DEBOUNCE_DELAY_MS)

  const handleSearchTermChange = useCallback(
    (newTerm: string) => {
      setSearchTerm(newTerm)
      debouncedSetSearchTerm(newTerm)
    },
    [debouncedSetSearchTerm]
  )

  const { data, fetchMore, networkStatus } = useDashboardsQueries({
    queryName: 'getWorkspaceJobsWithFoldersPaginated',
    queryVariables: {
      workspaceId,
      pagination: { take: JOBS_PAGE_SIZE },
      term: debouncedSearchTerm,
    },
    errorLog: GET_JOBS_WITH_FOLDERS_LOG,
  })

  const allJobs = useMemo(() => {
    const jobs = data?.listWorkspaceJobPaginated?.data ?? []
    return jobs.map(formatJobForFolderList)
  }, [data])

  const jobsList = useMemo(() => {
    return allJobs.slice(0, visibleCount)
  }, [allJobs, visibleCount])

  const resetJobsPagination = useCallback(() => {
    setVisibleCount(JOBS_PAGE_SIZE)
  }, [])

  const mostRecentJob = useMemo(() => {
    if (data?.me?.__typename === 'User') {
      const recent = data?.me?.userActivity?.mostRecentJob
      return recent ? formatJobForFolderList(recent) : null
    }
    return null
  }, [data])

  const jobsRows = data?.listWorkspaceJobPaginated?.totalCount ?? 0
  const cursor = data?.listWorkspaceJobPaginated?.cursor
  const hasMore = jobsList.length < jobsRows
  const isLoading = [NetworkStatus.loading, NetworkStatus.setVariables].includes(networkStatus)
  const isFetchingMore = networkStatus === NetworkStatus.fetchMore

  const loadMoreJobs = useCallback(() => {
    if (isLoading || NetworkStatus.fetchMore === networkStatus) return

    const nextCount = Math.min(visibleCount + JOBS_PAGE_SIZE, jobsRows)

    if (allJobs.length >= nextCount) {
      setVisibleCount(nextCount)
      return
    }

    if (!hasMore) return
    fetchMore({
      variables: {
        workspaceId,
        pagination: {
          take: JOBS_PAGE_SIZE,
          cursor,
        },
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev
        const prevData = prev.listWorkspaceJobPaginated.data
        const newData = fetchMoreResult.listWorkspaceJobPaginated.data

        const mergedData = [
          ...prevData,
          ...newData.filter(newJob => !prevData.some(prevJob => prevJob.id === newJob.id)),
        ]

        return {
          ...fetchMoreResult,
          listWorkspaceJobPaginated: {
            ...fetchMoreResult.listWorkspaceJobPaginated,
            data: mergedData,
          },
        }
      },
    })
    setVisibleCount(nextCount)
  }, [isLoading, networkStatus, visibleCount, allJobs.length, hasMore, fetchMore, workspaceId, cursor, jobsRows])

  return {
    jobsList,
    mostRecentJob,
    isLoading,
    isFetchingMore,
    jobsRows,
    searchTerm,
    handleSearchTermChange,
    resetJobsPagination,
    loadMoreJobs,
    hasMoreJobs: hasMore,
  }
}
